"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@clerk/nextjs";
import { Effect } from "effect";
import { RpcClient } from "@effect/rpc";
import { featureAnnouncementsUserEq, FeatureAnnouncementsUserRpc } from "@/lib/effect-query";
import type { AnnouncementWithSteps } from "@/presentation/rpc/feature-announcements-rpc";
import type { AnnouncementData } from "./types";

// ============================================================================
// Query Key Factory
// ============================================================================

export const GLOBAL_ANNOUNCEMENT_QUERY_KEYS = {
  all: ["feature-announcements", "global"] as const
};

// ============================================================================
// RPC-to-UI Mapping
// ============================================================================

function rpcToAnnouncementData(rpc: AnnouncementWithSteps): AnnouncementData {
  return {
    id: rpc.announcement.id,
    featureKey: rpc.announcement.featureKey,
    title: rpc.announcement.title,
    badgeText: rpc.announcement.badgeText,
    announcementType: rpc.announcement.announcementType ?? "global",
    steps: [...rpc.steps].sort((a, b) => a.sortOrder - b.sortOrder).map(s => ({
      sortOrder: s.sortOrder,
      description: s.description,
      mediaType: s.mediaType,
      mediaUrl: s.mediaUrl
    }))
  };
}

// ============================================================================
// Query Options
// ============================================================================

const globalAnnouncementQueryOptions = () => ({
  ...featureAnnouncementsUserEq.queryOptions({
    queryKey: GLOBAL_ANNOUNCEMENT_QUERY_KEYS.all,
    queryFn: () => Effect.gen(function* () {
      const client = yield* RpcClient.make(FeatureAnnouncementsUserRpc);
      return yield* client.getGlobalAnnouncement();
    }).pipe(Effect.scoped)
  }),
  staleTime: 5 * 60 * 1000,
  gcTime: 10 * 60 * 1000
});

// ============================================================================
// Hook
// ============================================================================

export function useGlobalAnnouncement() {
  const $ = _c(10);
  const {
    isSignedIn
  } = useAuth();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = globalAnnouncementQueryOptions();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = isSignedIn === true;
  let t2;
  if ($[1] !== t1) {
    t2 = {
      ...t0,
      enabled: t1
    };
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    data,
    isPending,
    isFetching,
    error
  } = useQuery(t2);
  const isLoading = isPending && isFetching;
  let t3;
  if ($[3] !== data) {
    t3 = data?.announcement ? rpcToAnnouncementData(data.announcement) : null;
    $[3] = data;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const announcement = t3;
  const hasSeen = data?.hasSeen ?? false;
  let t4;
  if ($[5] !== announcement || $[6] !== error || $[7] !== hasSeen || $[8] !== isLoading) {
    t4 = {
      data: announcement,
      hasSeen,
      isLoading,
      error
    };
    $[5] = announcement;
    $[6] = error;
    $[7] = hasSeen;
    $[8] = isLoading;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}